import type { HeroTypeConfig, HeroLevelConfig } from "@/types/hero.types"

// Re-export HeroTypeConfig as HeroConfig for backwards compatibility
export type { HeroTypeConfig as HeroConfig } from "@/types/hero.types"

export const heroLevelConfig: HeroLevelConfig = {
  maxLevel: 10,
  baseExperienceRequired: 100,
  experienceScalingFactor: 1.5,
  statMultiplierPerLevel: 0.15,
}

export const getExperienceForLevel = (level: number): number => {
  if (level >= heroLevelConfig.maxLevel) return Number.POSITIVE_INFINITY
  return Math.floor(
    heroLevelConfig.baseExperienceRequired * Math.pow(heroLevelConfig.experienceScalingFactor, level - 1),
  )
}

export const heroTypes: Record<string, HeroTypeConfig> = {
  paladin: {
    id: "paladin",
    name: "Paladin",
    displayName: "Sir Aldric",
    description: "Holy warrior with high health and defensive magic",
    portrait: "/images/heroes/portraits/portrait-paladin.png",
    baseStats: {
      health: 800,
      mana: 100,
      damage: { min: 12, max: 18 },
      armor: 3,
      speed: 1.2,
      attackRange: 35,
      attackSpeed: 50,
    },
    damageType: "hero",
    armorType: "hero",
    color: "#C0C0C0",
    abilities: [
      {
        id: "shield-bash",
        name: "Shield Bash",
        description: "Stun nearby enemies for 2 seconds",
        cooldown: 180,
        manaCost: 30,
        unlockCost: 100,
        isUnlocked: false,
      },
      {
        id: "war-cry",
        name: "War Cry",
        description: "Increase damage by 50% for 5 seconds",
        cooldown: 240,
        manaCost: 40,
        unlockCost: 150,
        isUnlocked: false,
      },
    ],
    skills: [
      {
        id: "vanguard-rush",
        name: "Vanguard Rush",
        description: "Click to dash toward target location, dealing damage and slowing all enemies in your path.",
        type: "dash",
        hotkey: "1",
        iconColor: "#4A90D9", // Steel blue
        icon: "/images/skills/paladin/1.png",
        maxLevel: 10,
        levels: [
          { damage: 25, range: 7.5, speed: 10, manaCost: 15, cooldown: 300, slowValue: 0.25, slowDuration: 180 }, // 5 sec cd, 3 sec slow
          { damage: 35, range: 7.5, speed: 10.5, manaCost: 17, cooldown: 285, slowValue: 0.25, slowDuration: 180 },
          { damage: 47, range: 8.25, speed: 11, manaCost: 19, cooldown: 270, slowValue: 0.25, slowDuration: 180 },
          { damage: 60, range: 8.25, speed: 11.5, manaCost: 21, cooldown: 255, slowValue: 0.25, slowDuration: 180 },
          { damage: 75, range: 9, speed: 12, manaCost: 23, cooldown: 240, slowValue: 0.25, slowDuration: 180 },
          { damage: 92, range: 9, speed: 12.5, manaCost: 25, cooldown: 225, slowValue: 0.25, slowDuration: 180 },
          { damage: 110, range: 9.75, speed: 13, manaCost: 27, cooldown: 210, slowValue: 0.25, slowDuration: 180 },
          { damage: 130, range: 9.75, speed: 13.5, manaCost: 29, cooldown: 195, slowValue: 0.25, slowDuration: 180 },
          { damage: 155, range: 10.5, speed: 14, manaCost: 31, cooldown: 180, slowValue: 0.25, slowDuration: 180 },
          { damage: 185, range: 10.5, speed: 15, manaCost: 35, cooldown: 150, slowValue: 0.25, slowDuration: 180 },
        ],
        currentLevel: 0,
        isUnlocked: false,
        isTargetable: true,
      },
      {
        id: "spectral-blade",
        name: "Spectral Blade",
        description: "Launch a spectral blade toward target location, piercing through enemies. No cooldown but high mana cost.",
        type: "projectile",
        hotkey: "2",
        iconColor: "#9932CC", // Purple
        icon: "/images/skills/paladin/2.png",
        maxLevel: 10,
        levels: [
          { damage: 50, range: 8, speed: 8, manaCost: 40, cooldown: 0, projectileSize: 20, piercing: true },
          { damage: 70, range: 8.5, speed: 8.5, manaCost: 45, cooldown: 0, projectileSize: 21, piercing: true },
          { damage: 95, range: 9, speed: 9, manaCost: 50, cooldown: 0, projectileSize: 22, piercing: true },
          { damage: 120, range: 9.5, speed: 9.5, manaCost: 55, cooldown: 0, projectileSize: 23, piercing: true },
          { damage: 150, range: 10, speed: 10, manaCost: 60, cooldown: 0, projectileSize: 24, piercing: true },
          { damage: 185, range: 10.5, speed: 10.5, manaCost: 65, cooldown: 0, projectileSize: 25, piercing: true },
          { damage: 225, range: 11, speed: 11, manaCost: 70, cooldown: 0, projectileSize: 26, piercing: true },
          { damage: 270, range: 11.5, speed: 11.5, manaCost: 75, cooldown: 0, projectileSize: 27, piercing: true },
          { damage: 320, range: 12, speed: 12, manaCost: 80, cooldown: 0, projectileSize: 28, piercing: true },
          { damage: 380, range: 12, speed: 13, manaCost: 90, cooldown: 0, projectileSize: 30, piercing: true },
        ],
        currentLevel: 0,
        isUnlocked: false,
        isTargetable: true,
      },
      {
        id: "empower-armor",
        name: "Empower Armor",
        description: "Activate to imbue your armor with protective magic. Reduces incoming damage and has a chance to heal when hit.",
        type: "buff",
        hotkey: "3",
        iconColor: "#FFD700", // Gold
        icon: "/images/skills/paladin/3.png",
        maxLevel: 10,
        levels: [
          { damage: 0, range: 0, speed: 0, manaCost: 20, cooldown: 1800, buffDuration: 900, healOnHitChance: 0.03, healAmount: 8, damageReduction: 0.05 }, // 5%
          { damage: 0, range: 0, speed: 0, manaCost: 20, cooldown: 1800, buffDuration: 960, healOnHitChance: 0.032, healAmount: 11, damageReduction: 0.07 }, // 7%
          { damage: 0, range: 0, speed: 0, manaCost: 20, cooldown: 1800, buffDuration: 1020, healOnHitChance: 0.034, healAmount: 14, damageReduction: 0.09 }, // 9%
          { damage: 0, range: 0, speed: 0, manaCost: 20, cooldown: 1800, buffDuration: 1080, healOnHitChance: 0.036, healAmount: 18, damageReduction: 0.11 }, // 11%
          { damage: 0, range: 0, speed: 0, manaCost: 20, cooldown: 1800, buffDuration: 1140, healOnHitChance: 0.038, healAmount: 22, damageReduction: 0.13 }, // 13%
          { damage: 0, range: 0, speed: 0, manaCost: 20, cooldown: 1800, buffDuration: 1200, healOnHitChance: 0.040, healAmount: 27, damageReduction: 0.15 }, // 15%
          { damage: 0, range: 0, speed: 0, manaCost: 20, cooldown: 1800, buffDuration: 1260, healOnHitChance: 0.042, healAmount: 32, damageReduction: 0.17 }, // 17%
          { damage: 0, range: 0, speed: 0, manaCost: 20, cooldown: 1800, buffDuration: 1320, healOnHitChance: 0.044, healAmount: 38, damageReduction: 0.19 }, // 19%
          { damage: 0, range: 0, speed: 0, manaCost: 20, cooldown: 1800, buffDuration: 1380, healOnHitChance: 0.046, healAmount: 45, damageReduction: 0.21 }, // 21%
          { damage: 0, range: 0, speed: 0, manaCost: 20, cooldown: 1800, buffDuration: 1500, healOnHitChance: 0.05, healAmount: 55, damageReduction: 0.25 }, // 25%
        ],
        currentLevel: 0, // Starts at 0, must be upgraded to unlock
        isUnlocked: false,
      },
      {
        id: "embolden",
        name: "Embolden",
        description: "Ultimate: Rally nearby towers, doubling their attack speed for a short duration. Unlockable every 6 levels.",
        type: "ultimate",
        hotkey: "4",
        iconColor: "#FFD700", // Gold
        icon: "/images/skills/paladin/4.png",
        maxLevel: 10,
        levels: [
          // Level 1: 3 tiles radius, 5s duration, 30s cooldown, 40 mana
          { damage: 0, range: 3, speed: 0, manaCost: 40, cooldown: 1800, towerFireRateMultiplier: 0.5, buffDuration: 300, aoeRadius: 192 },
          // Level 2: 3.5 tiles, 6s duration, 28s cooldown
          { damage: 0, range: 3.5, speed: 0, manaCost: 50, cooldown: 1680, towerFireRateMultiplier: 0.5, buffDuration: 360, aoeRadius: 224 },
          // Level 3: 4 tiles, 7s duration, 26s cooldown
          { damage: 0, range: 4, speed: 0, manaCost: 60, cooldown: 1560, towerFireRateMultiplier: 0.5, buffDuration: 420, aoeRadius: 256 },
          // Level 4: 4.5 tiles, 8s duration, 24s cooldown
          { damage: 0, range: 4.5, speed: 0, manaCost: 70, cooldown: 1440, towerFireRateMultiplier: 0.5, buffDuration: 480, aoeRadius: 288 },
          // Level 5: 5 tiles, 9s duration, 22s cooldown
          { damage: 0, range: 5, speed: 0, manaCost: 80, cooldown: 1320, towerFireRateMultiplier: 0.5, buffDuration: 540, aoeRadius: 320 },
          // Level 6: 5.5 tiles, 10s duration, 20s cooldown
          { damage: 0, range: 5.5, speed: 0, manaCost: 90, cooldown: 1200, towerFireRateMultiplier: 0.5, buffDuration: 600, aoeRadius: 352 },
          // Level 7: 6 tiles, 11s duration, 18s cooldown
          { damage: 0, range: 6, speed: 0, manaCost: 100, cooldown: 1080, towerFireRateMultiplier: 0.5, buffDuration: 660, aoeRadius: 384 },
          // Level 8: 6.5 tiles, 12s duration, 16s cooldown
          { damage: 0, range: 6.5, speed: 0, manaCost: 110, cooldown: 960, towerFireRateMultiplier: 0.5, buffDuration: 720, aoeRadius: 416 },
          // Level 9: 7 tiles, 13s duration, 14s cooldown
          { damage: 0, range: 7, speed: 0, manaCost: 120, cooldown: 840, towerFireRateMultiplier: 0.5, buffDuration: 780, aoeRadius: 448 },
          // Level 10: 7.5 tiles, 14s duration, 12s cooldown
          { damage: 0, range: 7.5, speed: 0, manaCost: 130, cooldown: 720, towerFireRateMultiplier: 0.5, buffDuration: 840, aoeRadius: 480 },
        ],
        currentLevel: 0, // Unlockable at hero level 6, 12, 18, etc.
        isUnlocked: false,
      },
    ],
    upgrades: [
      { stat: "health", level: 1, cost: 50, value: 1000 },
      { stat: "health", level: 2, cost: 75, value: 1250 },
      { stat: "health", level: 3, cost: 100, value: 1600 },
      { stat: "damage", level: 1, cost: 40, value: 20 },
      { stat: "damage", level: 2, cost: 60, value: 25 },
      { stat: "damage", level: 3, cost: 80, value: 35 },
      { stat: "speed", level: 1, cost: 30, value: 1.4 },
      { stat: "speed", level: 2, cost: 50, value: 1.6 },
      { stat: "attackSpeed", level: 1, cost: 35, value: 45 },
      { stat: "attackSpeed", level: 2, cost: 55, value: 40 },
    ],
  },
  ranger: {
    id: "ranger",
    name: "Ranger",
    displayName: "Sylara",
    description: "Fast ranged attacker with mobility",
    portrait: "/images/heroes/portraits/portrait-ranger.png",
    baseStats: {
      health: 500,
      mana: 80,
      damage: { min: 10, max: 14 },
      armor: 2,
      speed: 1.8,
      attackRange: 180,
      attackSpeed: 40,
    },
    damageType: "hero",
    armorType: "hero",
    color: "#228B22",
    abilities: [
      {
        id: "multi-shot",
        name: "Multi Shot",
        description: "Fire 5 arrows at once",
        cooldown: 150,
        manaCost: 25,
        unlockCost: 100,
        isUnlocked: false,
      },
      {
        id: "dash",
        name: "Dash",
        description: "Quickly move to target location",
        cooldown: 120,
        manaCost: 20,
        unlockCost: 120,
        isUnlocked: false,
      },
    ],
    skills: [
      {
        id: "wildkin-call",
        name: "Wildkin Call",
        description: "Summon a swift fox and a sturdy boar to fight by your side. Recast to heal and respawn.",
        type: "summon",
        hotkey: "1",
        iconColor: "#8B4513", // Saddle brown (earthy/nature)
        icon: "/images/skills/ranger/1.png",
        maxLevel: 10,
        levels: [
          { damage: 15, range: 2, speed: 0, manaCost: 40, cooldown: 1800, summonTypes: [{ type: "fox", health: 180 }, { type: "boar", health: 300 }] }, // 30 sec
          { damage: 19, range: 2, speed: 0, manaCost: 42, cooldown: 1710, summonTypes: [{ type: "fox", health: 230 }, { type: "boar", health: 385 }] }, // 28.5 sec
          { damage: 24, range: 2, speed: 0, manaCost: 44, cooldown: 1620, summonTypes: [{ type: "fox", health: 290 }, { type: "boar", health: 480 }] }, // 27 sec
          { damage: 30, range: 2, speed: 0, manaCost: 46, cooldown: 1530, summonTypes: [{ type: "fox", health: 360 }, { type: "boar", health: 600 }] }, // 25.5 sec
          { damage: 37, range: 2, speed: 0, manaCost: 48, cooldown: 1440, summonTypes: [{ type: "fox", health: 445 }, { type: "boar", health: 745 }] }, // 24 sec
          { damage: 45, range: 2, speed: 0, manaCost: 50, cooldown: 1350, summonTypes: [{ type: "fox", health: 545 }, { type: "boar", health: 910 }] }, // 22.5 sec
          { damage: 54, range: 2, speed: 0, manaCost: 52, cooldown: 1260, summonTypes: [{ type: "fox", health: 660 }, { type: "boar", health: 1100 }] }, // 21 sec
          { damage: 65, range: 2, speed: 0, manaCost: 54, cooldown: 1170, summonTypes: [{ type: "fox", health: 790 }, { type: "boar", health: 1320 }] }, // 19.5 sec
          { damage: 78, range: 2, speed: 0, manaCost: 56, cooldown: 1080, summonTypes: [{ type: "fox", health: 935 }, { type: "boar", health: 1560 }] }, // 18 sec
          { damage: 95, range: 2, speed: 0, manaCost: 60, cooldown: 900, summonTypes: [{ type: "fox", health: 1115 }, { type: "boar", health: 1860 }] }, // 15 sec
        ],
        currentLevel: 0,
        isUnlocked: false,
      },
      {
        id: "entangling-roots",
        name: "Entangling Roots",
        description: "Click a location to summon roots that root enemies for 5 seconds, dealing heavy damage over time. Nearby enemies take minor damage.",
        type: "targeted-dot",
        hotkey: "2",
        iconColor: "#228B22", // Forest green
        icon: "/images/skills/ranger/2.png",
        maxLevel: 10,
        levels: [
          { damage: 80, range: 8, speed: 0, manaCost: 35, cooldown: 900, dotDamage: 16, dotTicks: 5, aoeRadius: 1.5, aoeDamage: 5, rootDuration: 300 }, // 15 sec
          { damage: 100, range: 8, speed: 0, manaCost: 37, cooldown: 870, dotDamage: 20, dotTicks: 5, aoeRadius: 1.5, aoeDamage: 6, rootDuration: 300 }, // 14.5 sec
          { damage: 125, range: 8, speed: 0, manaCost: 39, cooldown: 840, dotDamage: 25, dotTicks: 5, aoeRadius: 1.75, aoeDamage: 8, rootDuration: 300 }, // 14 sec
          { damage: 155, range: 8.5, speed: 0, manaCost: 41, cooldown: 810, dotDamage: 31, dotTicks: 5, aoeRadius: 1.75, aoeDamage: 10, rootDuration: 300 }, // 13.5 sec
          { damage: 190, range: 8.5, speed: 0, manaCost: 43, cooldown: 780, dotDamage: 38, dotTicks: 5, aoeRadius: 2, aoeDamage: 12, rootDuration: 300 }, // 13 sec
          { damage: 230, range: 9, speed: 0, manaCost: 45, cooldown: 750, dotDamage: 46, dotTicks: 5, aoeRadius: 2, aoeDamage: 15, rootDuration: 300 }, // 12.5 sec
          { damage: 275, range: 9, speed: 0, manaCost: 47, cooldown: 720, dotDamage: 55, dotTicks: 5, aoeRadius: 2.25, aoeDamage: 18, rootDuration: 300 }, // 12 sec
          { damage: 325, range: 9.5, speed: 0, manaCost: 49, cooldown: 690, dotDamage: 65, dotTicks: 5, aoeRadius: 2.25, aoeDamage: 22, rootDuration: 300 }, // 11.5 sec
          { damage: 385, range: 9.5, speed: 0, manaCost: 52, cooldown: 660, dotDamage: 77, dotTicks: 5, aoeRadius: 2.5, aoeDamage: 26, rootDuration: 300 }, // 11 sec
          { damage: 450, range: 10, speed: 0, manaCost: 55, cooldown: 600, dotDamage: 90, dotTicks: 5, aoeRadius: 2.5, aoeDamage: 32, rootDuration: 300 }, // 10 sec
        ],
        currentLevel: 0,
        isUnlocked: false,
        isTargetable: true,
        targetType: "ground",
      },
      {
        id: "see-the-wind",
        name: "See the Wind",
        description: "Passive. Your attacks have a chance to deal critical damage.",
        type: "passive",
        hotkey: "3",
        iconColor: "#87CEEB", // Sky blue
        icon: "/images/skills/ranger/3.png",
        maxLevel: 10,
        levels: [
          { damage: 0, range: 0, speed: 0, manaCost: 0, cooldown: 0, critChance: 0.16, critMultiplier: 11 }, // 16% chance, 11x
          { damage: 0, range: 0, speed: 0, manaCost: 0, cooldown: 0, critChance: 0.17, critMultiplier: 12 }, // 17% chance, 12x
          { damage: 0, range: 0, speed: 0, manaCost: 0, cooldown: 0, critChance: 0.18, critMultiplier: 13 }, // 18% chance, 13x
          { damage: 0, range: 0, speed: 0, manaCost: 0, cooldown: 0, critChance: 0.19, critMultiplier: 14 }, // 19% chance, 14x
          { damage: 0, range: 0, speed: 0, manaCost: 0, cooldown: 0, critChance: 0.20, critMultiplier: 15 }, // 20% chance, 15x
          { damage: 0, range: 0, speed: 0, manaCost: 0, cooldown: 0, critChance: 0.21, critMultiplier: 16 }, // 21% chance, 16x
          { damage: 0, range: 0, speed: 0, manaCost: 0, cooldown: 0, critChance: 0.22, critMultiplier: 17 }, // 22% chance, 17x
          { damage: 0, range: 0, speed: 0, manaCost: 0, cooldown: 0, critChance: 0.23, critMultiplier: 18 }, // 23% chance, 18x
          { damage: 0, range: 0, speed: 0, manaCost: 0, cooldown: 0, critChance: 0.24, critMultiplier: 19 }, // 24% chance, 19x
          { damage: 0, range: 0, speed: 0, manaCost: 0, cooldown: 0, critChance: 0.25, critMultiplier: 20 }, // 25% chance, 20x
        ],
        currentLevel: 0, // Starts at 0, must be upgraded to unlock
        isUnlocked: false,
      },
      {
        id: "backfire-aura",
        name: "Backfire Aura",
        description: "Ultimate Passive: You and your wildkin are surrounded by a damage shield that returns a percentage of melee damage to attackers.",
        type: "ultimate",
        hotkey: "4",
        iconColor: "#9400D3", // Dark violet
        icon: "/images/skills/ranger/4.png",
        maxLevel: 10,
        levels: [
          // Each level increases damage reflection and aura radius
          { damage: 0, range: 0, speed: 0, manaCost: 0, cooldown: 0, damageReflection: 0.09, aoeRadius: 300 }, // 9%, 300px
          { damage: 0, range: 0, speed: 0, manaCost: 0, cooldown: 0, damageReflection: 0.18, aoeRadius: 400 }, // 18%, 400px
          { damage: 0, range: 0, speed: 0, manaCost: 0, cooldown: 0, damageReflection: 0.27, aoeRadius: 500 }, // 27%, 500px
          { damage: 0, range: 0, speed: 0, manaCost: 0, cooldown: 0, damageReflection: 0.36, aoeRadius: 600 }, // 36%, 600px
          { damage: 0, range: 0, speed: 0, manaCost: 0, cooldown: 0, damageReflection: 0.45, aoeRadius: 700 }, // 45%, 700px
          { damage: 0, range: 0, speed: 0, manaCost: 0, cooldown: 0, damageReflection: 0.54, aoeRadius: 800 }, // 54%, 800px
          { damage: 0, range: 0, speed: 0, manaCost: 0, cooldown: 0, damageReflection: 0.63, aoeRadius: 900 }, // 63%, 900px
          { damage: 0, range: 0, speed: 0, manaCost: 0, cooldown: 0, damageReflection: 0.72, aoeRadius: 1000 }, // 72%, 1000px
          { damage: 0, range: 0, speed: 0, manaCost: 0, cooldown: 0, damageReflection: 0.81, aoeRadius: 1100 }, // 81%, 1100px
          { damage: 0, range: 0, speed: 0, manaCost: 0, cooldown: 0, damageReflection: 0.90, aoeRadius: 1200 }, // 90%, 1200px
        ],
        currentLevel: 0, // Unlockable at hero level 6, 12, 18, etc.
        isUnlocked: false,
      },
    ],
    upgrades: [
      { stat: "health", level: 1, cost: 40, value: 650 },
      { stat: "health", level: 2, cost: 60, value: 850 },
      { stat: "health", level: 3, cost: 80, value: 1100 },
      { stat: "damage", level: 1, cost: 50, value: 16 },
      { stat: "damage", level: 2, cost: 70, value: 20 },
      { stat: "damage", level: 3, cost: 90, value: 28 },
      { stat: "speed", level: 1, cost: 35, value: 2.0 },
      { stat: "speed", level: 2, cost: 55, value: 2.3 },
      { stat: "attackSpeed", level: 1, cost: 40, value: 35 },
      { stat: "attackSpeed", level: 2, cost: 60, value: 30 },
    ],
  },
  siegesmith: {
    id: "siegesmith",
    name: "Siegesmith",
    displayName: "Durn Ironfist",
    description:
      "Dwarven warrior who dual-wields an axe (Ash) and warhammer (Ember). High health tank with tower-building abilities.",
    portrait: "/images/heroes/portraits/portrait-siegesmith.png",
    baseStats: {
      health: 1200,
      mana: 120,
      damage: { min: 6, max: 10 },
      armor: 5,
      speed: 0.9,
      attackRange: 35,
      attackSpeed: 60,
    },
    damageType: "siege",
    armorType: "heavy",
    color: "#8B4513",
    abilities: [
      {
        id: "cleaving-strike",
        name: "Cleaving Strike",
        description: "Ash cuts through multiple enemies in an arc, dealing damage to all nearby foes",
        cooldown: 120,
        manaCost: 25,
        unlockCost: 100,
        isUnlocked: false,
      },
      {
        id: "rending-blow",
        name: "Rending Blow",
        description: "A powerful axe strike that reduces enemy armor for 5 seconds",
        cooldown: 180,
        manaCost: 35,
        unlockCost: 150,
        isUnlocked: false,
      },
      {
        id: "fortify-tower",
        name: "Fortify Tower",
        description: "Ember reinforces a nearby tower, increasing its health and damage for 10 seconds",
        cooldown: 200,
        manaCost: 40,
        unlockCost: 120,
        isUnlocked: false,
      },
      {
        id: "emergency-repair",
        name: "Emergency Repair",
        description: "Quickly repair a damaged tower, restoring 50% of its max health",
        cooldown: 240,
        manaCost: 50,
        unlockCost: 180,
        isUnlocked: false,
      },
    ],
    skills: [
      {
        id: "forge-turret",
        name: "Forge Turret",
        description: "Click a location to construct a siege turret. Up to 3 turrets can be active. Oldest turret is destroyed when placing a 4th.",
        type: "summon",
        hotkey: "1",
        iconColor: "#CD7F32", // Bronze
        icon: "/images/skills/siegesmith/1.png",
        maxLevel: 10,
        levels: [
          // Damage reduced by 75%, cooldown 4x faster, max 3 turrets active
          { damage: 2, range: 5, speed: 0, manaCost: 50, cooldown: 600, summonCount: 1, summonHealth: 400, summonType: "siege-tower", maxSummons: 3 }, // 10 sec
          { damage: 3, range: 5.25, speed: 0, manaCost: 52, cooldown: 570, summonCount: 1, summonHealth: 500, summonType: "siege-tower", maxSummons: 3 }, // 9.5 sec
          { damage: 4, range: 5.5, speed: 0, manaCost: 54, cooldown: 540, summonCount: 1, summonHealth: 625, summonType: "siege-tower", maxSummons: 3 }, // 9 sec
          { damage: 5, range: 5.75, speed: 0, manaCost: 56, cooldown: 510, summonCount: 1, summonHealth: 775, summonType: "siege-tower", maxSummons: 3 }, // 8.5 sec
          { damage: 6, range: 6, speed: 0, manaCost: 58, cooldown: 480, summonCount: 1, summonHealth: 950, summonType: "siege-tower", maxSummons: 3 }, // 8 sec
          { damage: 7, range: 6.25, speed: 0, manaCost: 60, cooldown: 450, summonCount: 1, summonHealth: 1150, summonType: "siege-tower", maxSummons: 3 }, // 7.5 sec
          { damage: 9, range: 6.5, speed: 0, manaCost: 62, cooldown: 420, summonCount: 1, summonHealth: 1400, summonType: "siege-tower", maxSummons: 3 }, // 7 sec
          { damage: 10, range: 6.75, speed: 0, manaCost: 64, cooldown: 390, summonCount: 1, summonHealth: 1700, summonType: "siege-tower", maxSummons: 3 }, // 6.5 sec
          { damage: 12, range: 7, speed: 0, manaCost: 66, cooldown: 360, summonCount: 1, summonHealth: 2050, summonType: "siege-tower", maxSummons: 3 }, // 6 sec
          { damage: 15, range: 7.5, speed: 0, manaCost: 70, cooldown: 300, summonCount: 1, summonHealth: 2500, summonType: "siege-tower", maxSummons: 3 }, // 5 sec
        ],
        currentLevel: 0,
        isUnlocked: false,
        isTargetable: true,
      },
      {
        id: "tremor-slam",
        name: "Tremor Slam",
        description: "Slam the ground, creating three seismic waves that radiate outward. Deals damage and slows enemies by 75% for 5 seconds.",
        type: "aoe",
        hotkey: "2",
        iconColor: "#8B4513", // Saddle brown
        icon: "/images/skills/siegesmith/2.png",
        maxLevel: 10,
        // Note: Damage is split across 3 waves (50%/25%/25%)
        levels: [
          { damage: 10, range: 5, speed: 0, manaCost: 30, cooldown: 600, slowValue: 0.25, slowDuration: 300 }, // 10 sec
          { damage: 15, range: 5.5, speed: 0, manaCost: 32, cooldown: 576, slowValue: 0.25, slowDuration: 300 }, // 9.6 sec
          { damage: 21, range: 6, speed: 0, manaCost: 34, cooldown: 552, slowValue: 0.25, slowDuration: 300 }, // 9.2 sec
          { damage: 28, range: 6.5, speed: 0, manaCost: 36, cooldown: 528, slowValue: 0.25, slowDuration: 300 }, // 8.8 sec
          { damage: 36, range: 7, speed: 0, manaCost: 38, cooldown: 504, slowValue: 0.25, slowDuration: 300 }, // 8.4 sec
          { damage: 45, range: 7.5, speed: 0, manaCost: 40, cooldown: 480, slowValue: 0.25, slowDuration: 300 }, // 8 sec
          { damage: 55, range: 8, speed: 0, manaCost: 42, cooldown: 456, slowValue: 0.25, slowDuration: 300 }, // 7.6 sec
          { damage: 67, range: 8.5, speed: 0, manaCost: 44, cooldown: 432, slowValue: 0.25, slowDuration: 300 }, // 7.2 sec
          { damage: 80, range: 9, speed: 0, manaCost: 46, cooldown: 408, slowValue: 0.25, slowDuration: 300 }, // 6.8 sec
          { damage: 100, range: 10, speed: 0, manaCost: 50, cooldown: 360, slowValue: 0.25, slowDuration: 300 }, // 6 sec
        ],
        currentLevel: 0,
        isUnlocked: false,
      },
      {
        id: "dwarven-treasury",
        name: "Dwarven Treasury",
        description: "Passive: Your dwarven business acumen permanently increases the interest rate earned between waves. (+0.75% per level)",
        type: "passive",
        hotkey: "3",
        iconColor: "#FFD700", // Gold
        icon: "/images/skills/siegesmith/3.png",
        maxLevel: 10,
        levels: [
          // Base interest is 5%. Each level adds +0.75% bonus interest (increased from 0.5%)
          // Level 1: +0.75% interest (5.75% total), costs 25 gold, 15 mana
          { damage: 0, range: 0, speed: 0, manaCost: 15, cooldown: 0, interestBonus: 0.0075, goldCost: 25 },
          // Level 2: +1.5% interest (6.5% total), costs 50 gold, 20 mana
          { damage: 0, range: 0, speed: 0, manaCost: 20, cooldown: 0, interestBonus: 0.015, goldCost: 50 },
          // Level 3: +2.25% interest (7.25% total), costs 80 gold, 25 mana
          { damage: 0, range: 0, speed: 0, manaCost: 25, cooldown: 0, interestBonus: 0.0225, goldCost: 80 },
          // Level 4: +3.0% interest (8.0% total), costs 120 gold, 30 mana
          { damage: 0, range: 0, speed: 0, manaCost: 30, cooldown: 0, interestBonus: 0.03, goldCost: 120 },
          // Level 5: +3.75% interest (8.75% total), costs 175 gold, 40 mana
          { damage: 0, range: 0, speed: 0, manaCost: 40, cooldown: 0, interestBonus: 0.0375, goldCost: 175 },
          // Level 6: +4.5% interest (9.5% total), costs 250 gold, 50 mana
          { damage: 0, range: 0, speed: 0, manaCost: 50, cooldown: 0, interestBonus: 0.045, goldCost: 250 },
          // Level 7: +5.25% interest (10.25% total), costs 350 gold, 65 mana
          { damage: 0, range: 0, speed: 0, manaCost: 65, cooldown: 0, interestBonus: 0.0525, goldCost: 350 },
          // Level 8: +6.0% interest (11.0% total), costs 500 gold, 80 mana
          { damage: 0, range: 0, speed: 0, manaCost: 80, cooldown: 0, interestBonus: 0.06, goldCost: 500 },
          // Level 9: +6.75% interest (11.75% total), costs 700 gold, 100 mana
          { damage: 0, range: 0, speed: 0, manaCost: 100, cooldown: 0, interestBonus: 0.0675, goldCost: 700 },
          // Level 10: +7.5% interest (12.5% total), costs 1000 gold, 120 mana
          { damage: 0, range: 0, speed: 0, manaCost: 120, cooldown: 0, interestBonus: 0.075, goldCost: 1000 },
        ],
        currentLevel: 0, // Starts at 0, must be upgraded to unlock
        isUnlocked: false,
      },
      {
        id: "traders-gambit",
        name: "Trader's Gambit",
        description: "Ultimate: The basis of being a good dealer is to have friends. Gain a random item. Each level unlocks new items in the shop.",
        type: "ultimate",
        hotkey: "4",
        iconColor: "#FFD700", // Gold
        icon: "/images/skills/siegesmith/4.png",
        maxLevel: 10,
        levels: [
          // Each level unlocks new items in the shop and allows spawning items of that tier
          // 80 second cooldown, mana cost increases per level
          { damage: 0, range: 0, speed: 0, manaCost: 20, cooldown: 4800, spawnItemLevel: 1 },  // Cebi Roots (20g)
          { damage: 0, range: 0, speed: 0, manaCost: 26, cooldown: 4800, spawnItemLevel: 2 },  // Siegesmith's Potion (200g)
          { damage: 0, range: 0, speed: 0, manaCost: 33, cooldown: 4800, spawnItemLevel: 3 },  // Greater Hisan Salve (1000g)
          { damage: 0, range: 0, speed: 0, manaCost: 43, cooldown: 4800, spawnItemLevel: 4 },  // Scroll of Teleportation (500g)
          { damage: 0, range: 0, speed: 0, manaCost: 57, cooldown: 4800, spawnItemLevel: 5 },  // Lure Pouch (2500g)
          { damage: 0, range: 0, speed: 0, manaCost: 74, cooldown: 4800, spawnItemLevel: 6 },  // Orb of Absorption (1500g)
          { damage: 0, range: 0, speed: 0, manaCost: 96, cooldown: 4800, spawnItemLevel: 7 },  // Ivory Mail (15000g)
          { damage: 0, range: 0, speed: 0, manaCost: 125, cooldown: 4800, spawnItemLevel: 8 }, // Sunstone (7500g)
          { damage: 0, range: 0, speed: 0, manaCost: 163, cooldown: 4800, spawnItemLevel: 9 }, // Dwarven Pride (25000g)
          { damage: 0, range: 0, speed: 0, manaCost: 212, cooldown: 4800, spawnItemLevel: 10 }, // Bloodstone (30000g), Greater Healing Potion (1000g)
        ],
        currentLevel: 0, // Unlockable at hero level 6, 12, 18, etc.
        isUnlocked: false,
      },
    ],
    upgrades: [
      { stat: "health", level: 1, cost: 60, value: 1500 },
      { stat: "health", level: 2, cost: 90, value: 1900 },
      { stat: "health", level: 3, cost: 120, value: 2400 },
      { stat: "damage", level: 1, cost: 35, value: 12 },
      { stat: "damage", level: 2, cost: 50, value: 15 },
      { stat: "damage", level: 3, cost: 70, value: 20 },
      { stat: "speed", level: 1, cost: 40, value: 1.0 },
      { stat: "speed", level: 2, cost: 60, value: 1.1 },
      { stat: "attackSpeed", level: 1, cost: 45, value: 55 },
      { stat: "attackSpeed", level: 2, cost: 65, value: 50 },
    ],
  },
}
